











































import {
  computed,
  defineComponent,
  toRefs,
  ref,
  onUnmounted,
  getCurrentInstance,
  reactive,
  watch,
} from '@vue/composition-api';
import { DialogPlugin } from 'tdesign-vue';
import { AddIcon, DeleteIcon } from 'tdesign-icons-vue';

// 引入组件
import InviteMembers from '@/renderer/views/desktop/FileManagement/CollaborationSpace/components/InviteMembers.vue';
import TeamOrMemberItem from '@/renderer/components/drawer/MemberManage/components/TeamOrMemberItem.vue';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入 compositions
import inviteInternalMemberHandler, {
  InviteMemberChangedParams,
} from '@/renderer/views/desktop/FileManagement/CollaborationSpace/compositions/inviteInternalMemberHandler';
import useMembers, { GroupSpaceInfo } from '@/renderer/components/dialog/AddTransfer/AddMember/compositions/useMembers';

// 引入工具类
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import { LOCATION_MAP } from '@/common/reporter/utils';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

/**
 * 协作空间共享管理
 */
export default defineComponent({
  name: 'collaborationMemberManage',
  props: {
    params: {
      required: false,
      type: Object as () => Drawer.CollaborationAuthParams<SMH.ColleborationSpaceInfoData>,
      default: null,
    },
  },
  components: {
    AddIcon,
    TeamOrMemberItem,
    DeleteIcon,
    InviteMembers,
  },
  setup(props, context) {
    const { params } = toRefs(props);
    const selected = computed(() => params.value?.selectedItem);
    const vm = getCurrentInstance()?.proxy || context.root;
    const store = vm.$store;
    const roles = computed(() => vm.$store.getters.roles);
    const defaultRoleId = computed(() => vm.$store.getters.defaultRoleId);
    const currentOrgId = computed(() => vm.$store.getters.orgId);
    const showInviteMemberGuide = ref(false);
    // 当前人是否是群组创建者(且是创建者所在企业）
    const isManager = computed(
      () => selected.value?.ownerId === vm.$store.getters.userId && selected.value?.orgId === vm.$store.getters.orgId,
    );
    const showInviteMembers = ref(false);
    // 群组
    const group = computed(() => selected.value || {});
    // 群组ID
    const groupId = computed(() => selected.value?.id || +vm.$route.query?.id || 0);
    const spaceOrgId = computed(() => selected.value?.orgId || +vm.$route.query?.spaceOrgId || store.getters.orgId);

    const groupSpaceInfo = computed<GroupSpaceInfo>(() => {
      return {
        type: 'group',
        groupId: groupId.value,
        spaceOrgId: spaceOrgId.value,
      };
    });
    const { loading, asyncLoading, memberData, methods: memberMethods } = useMembers(groupSpaceInfo);
    const sortOriginMembers = computed(() => methods.sortPermission(memberData.list as CoFile.TeamOrMemberItem[]));

    const { methods: crudMethods } = inviteInternalMemberHandler(groupId);

    const notMatchOrg = computed(
      () => typeof selected.value?.orgId === 'number' && selected.value?.orgId !== currentOrgId.value,
    );
    // 对方组织的roleList，当前人和群主所在的组织不一致的情况下出现（因为此时的roleId不一致）
    const managerRoleMap = ref<Map<Number, String>>(new Map<Number, String>());

    const methods = {
      async initMembers(): Promise<boolean> {
        try {
          // 若组织ID和群组的组织不一样
          if (notMatchOrg.value) {
            const params = {
              groupId: (vm.$route.name === 'collaborationSpaceList' ? +vm.$route.query?.id : 0) || selected.value?.id,
              spaceOrgId: spaceOrgId.value,
            };
            const {
              data: { roleList },
            } = await smh.userAPI.getCollaborationSpaceInfo(params);
            // 转一下map 让子元素都好查询
            roleList.map((item: SMH.Role) => managerRoleMap.value.set(item.id, item.name));
          }
          memberMethods.resetData();
          await memberMethods.getMembers({ pageSize: 30 });
          return true;
        } catch (error: MsgError) {
          crudMethods.handlerError(error);
          return false;
        }
      },
      async refresh(params?: { isSuccess: boolean }) {
        await methods.initMembers();
        params?.isSuccess && ipc.emit(ipc.RENDERER_EVENTS_MAP.DRAWER_MANAGER_MEMBER);
      },
      // 弹窗新加用户
      async onAddMember(params: InviteMemberChangedParams) {
        const isSuccess = await crudMethods.onChangeInviteMembers(params);
        if (isSuccess) {
          await methods.refresh();
          showInviteMembers.value = false;
        }
      },
      async onAuthOperate(action: string, item?: CoFile.TeamOrMemberItem) {
        if (action === 'delete') {
          const deleteDialog = CustomDialog.confirm({
            header: i18n_t('提示'),
            body: i18n_t('是否要删除成员{slot1}？', {
              slot1: (item as CoFile.TeamOrMemberItem).name,
            }),
            theme: 'warning',
            onConfirm: async () => {
              const deleted = [item as CoFile.TeamOrMemberItem];
              const res = await crudMethods.deleteMembers(deleted);
              if (deleteDialog.hide) {
                deleteDialog.hide();
              }
              if (res) {
                tips.success(i18n_t('删除成功'));
              }
              methods.refresh();
              ipc.emit(ipc.RENDERER_EVENTS_MAP.DRAWER_MANAGER_MEMBER);
              reporter.report('member', {
                operateType: 'del',
                operateLocation: LOCATION_MAP[vm.$route.name as string],
              });
            },
          });
        }
        if (action === 'quit') {
          const quitDialog = CustomDialog.confirm({
            header: i18n_t('确定退出？'),
            body: i18n_t('退出后将无法查看群组及群组内共享的文件。'),
            theme: 'warning',
            onConfirm: async () => {
              const quit = [item as CoFile.TeamOrMemberItem];
              const res = await crudMethods.quit(quit);
              if (quitDialog.hide) {
                quitDialog.hide();
              }
              if (res) {
                tips.success(i18n_t('退出成功'));
                reporter.report('member', {
                  operateType: 'del',
                  operateLocation: LOCATION_MAP[vm.$route.name as string],
                });
                // FIX: 若是在协作群组中退群，则路由回退到协作空间
                if (vm.$route.name !== 'collaborationSpace') {
                  dispatchRouter({
                    name: 'collaborationSpace',
                  });
                } else {
                  ipc.emit(ipc.RENDERER_EVENTS_MAP.DRAWER_MANAGER_MEMBER);
                }
              }
            },
          });
        }
        if (action === 'edit') {
          const changed = [item as CoFile.TeamOrMemberItem];
          const res = await crudMethods.updateMembers(changed);
          if (res) {
            tips.success(i18n_t('修改成功'));
          }
          methods.refresh();
          ipc.emit(ipc.RENDERER_EVENTS_MAP.DRAWER_MANAGER_MEMBER);
        }
      },
      async onManageDialog() {
        showInviteMembers.value = true;
        reporter.report('group', {
          group_invite: 'inner',
          group_name: group.value.name,
        });
      },
      sortPermission(list: CoFile.TeamOrMemberItem[]): CoFile.TeamOrMemberItem[] {
        const ownerRoleId = vm.$store.getters.ownerRole?.id;
        return list.sort((a, b) => {
          // 所有者权限排最前面
          if (a.roleId) {
            if (a.roleId !== ownerRoleId && b.roleId === ownerRoleId) return 1;
          }
          if (a.teamRoleId) {
            if (a.teamRoleId !== ownerRoleId && b.teamRoleId === ownerRoleId) return 1;
          }
          // 带标签权限排在不带标签之前
          if (a.tag) {
            if (a.tag !== 0 && b.tag === 0) return -1;
          }
          if (a.disabledOperate) {
            // 不支持操作的排序到最前
            if (a.disabledOperate && !b.disabledOperate) return -1;
          }
          return 0;
        });
      },
      onScroll(options: { e: Event | WheelEvent; scrollTop: number; scrollBottom: number }) {
        const { scrollBottom } = options;
        if (scrollBottom <= 20) {
          memberMethods.getMembers({ pageSize: 30 });
        }
      },
    };

    methods.refresh();
    // 监听刷新数据
    watch(showInviteMembers, val => {
      if (!val) methods.refresh();
    });

    const guideTipsShow = (val: string) => {
      if (val === 'showInviteMemberGuide') {
        showInviteMemberGuide.value = true;
        setTimeout(() => {
          showInviteMemberGuide.value = false;
        }, 3000);
      }
    };
    ipc.on(ipc.RENDERER_EVENTS_MAP.GUIDE_SHOW_GUIDE_TIPS, guideTipsShow);
    watch(params, val => {
      if (val) {
        methods.refresh();
      }
    });
    onUnmounted(() => {
      ipc.off(ipc.RENDERER_EVENTS_MAP.GUIDE_SHOW_GUIDE_TIPS, guideTipsShow);
    });

    return {
      roles,
      sortOriginMembers,
      group,
      loading,
      asyncLoading,
      memberData,
      managerRoleMap,
      showInviteMembers,
      defaultRoleId,
      ...methods,
      isManager,
      showInviteMemberGuide,
    };
  },
});
